import {
  Component,
  signal,
  computed,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GeographyService } from '../../../../../core/services/geography.service';
import { ProfessionalsService } from '../../../../../core/services/professionals.service';
import { UserSessionService } from '../../../../../core/services/mock-auth.service';
import { ProfessionalsWizardService } from '../../../../services/professionals-wizard.service';
import { ProfessionalsDataService } from '../../../../services/professionals-data.service';
import { convertEmptyStringsToNull } from '../../../../utils/form-data.utils';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { FileUploadModule } from 'primeng/fileupload';
import { ProfessionalDropdownOption, CollaboratorUI } from '../../../../../core/models/professional.models';
import { CompetenceArea } from '../../../../models';
import { hasMeaningfulText } from '../../../../../core/validators/text-value.utils';
import { ValidationErrorSection, validationSectionsFromMessages } from '../../../validation-error-summary/validation-error-summary.model';
import { OperationalSitesSectionComponent } from '../operational-sites/operational-sites-section.component';
import {
  OperatingSiteVM,
  sitesFromResponse,
  sitesToRequest,
  validateOperationalSites,
} from '../operational-sites/operational-sites.model';

@Component({
  selector: 'app-lawyer-form',
  standalone: true,
  styleUrl: './shared-form-styles.scss',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    CheckboxModule,
    RadioButtonModule,
    ToastModule,
    TooltipModule,
    FileUploadModule,
    OperationalSitesSectionComponent,
  ],
  templateUrl: './lawyer-form.component.html',
})
export class LawyerFormComponent implements OnInit {
  readonly hasMeaningfulText = hasMeaningfulText;
  readonly today = new Date();

  private readonly geographyService = inject(GeographyService);
  private readonly professionalsService = inject(ProfessionalsService);
  private readonly mockAuthService = inject(UserSessionService);
  private readonly wizardService = inject(ProfessionalsWizardService);
  private readonly titleService = inject(Title);
  private readonly messageService = inject(MessageService);
  private professionalsDataService = inject(ProfessionalsDataService);

  // Optional professional ID from parent component (for review page)
  professionalIdInput = input<string | null>(null);
  // Explicit readonly flag passed by parent
  readonly = input<boolean>(false);

  isReadOnly = computed(() => this.professionalsDataService.isReadOnly() || this.readonly());

  @Output() onSave = new EventEmitter<any>();
  @Output() onSaveDraft = new EventEmitter<any>();
  @Output() validationFailed = new EventEmitter<ValidationErrorSection[]>();

  @ViewChildren('formField') formFields!: QueryList<ElementRef>;

  // Track field touch state for validation
  touchedFields = new Set<string>();
  formSubmitted = signal<boolean>(false);
  liveRegionMessage = signal<string>('');

  // Section 1: Specific Information (all required)
  activityStartDate = signal<Date | null>(null);
  professionalOrder = signal<string>('');
  orderRegistrationNumber = signal<number | null>(null);
  assignedCompanies = signal<string>('');
  operatingTerritories = signal<string[]>([]);

  // Section 2: Forma di attività
  activityForm = signal<string>('');
  collectiveType = signal<string>('');
  firmName = signal<string>('');

  // Section 2: Sede operativa (multi-sede, gestita da app-operational-sites-section)
  operatingSameAsLegal = signal<boolean>(true);
  // Id della sede "legalFallback" persistita (round-trip per preservare i codici SOL associati)
  operatingLegalSiteId = signal<number | null>(null);
  operatingSites = signal<OperatingSiteVM[]>([]);
  rctPolicyNumber = signal<number | null>(null); // Required
  insuranceCompany = signal<string>('');
  coverageLimit = signal<number | null>(null);
  agency = signal<string>('');

  // Section 4: Collaborators
  collaborators = signal<CollaboratorUI[]>([]);
  private collaboratorIdCounter = 0;

  // Section 5: Competence Areas
  competenceAreas = signal<CompetenceArea[]>([]);
  private competenceAreaIdCounter = 0;

  // Dropdown options
  activityFormOptions = signal<ProfessionalDropdownOption[]>([
    { label: 'Individuale', value: 'INDIVIDUAL' },
    { label: 'Collettiva', value: 'COLLECTIVE' },
    { label: 'Società', value: 'COMPANY' },
  ]);
  collectiveTypeOptions = signal<ProfessionalDropdownOption[]>([
    { label: 'SRL', value: 'SRL' },
    { label: 'STP', value: 'STP' },
    { label: 'Associazione Professionale', value: 'ASSOCIATION' },
    { label: 'Altro', value: 'OTHER' },
  ]);
  allProvincesOptions = signal<ProfessionalDropdownOption[]>([]); // For operating territories

  // Readonly: etichette dei territori operativi selezionati, risolte dalle
  // province (il p-multiselect disabilitato non mostra i chip selezionati).
  operatingTerritoriesLabels = computed(() => {
    const options = this.allProvincesOptions();
    return this.operatingTerritories().map(
      (value) => options.find((option) => option.value === value)?.label ?? value,
    );
  });

  // Loading states
  isLoadingData = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  hadPreviousCollaborators = signal<boolean>(false);
  hadPreviousCompetenceAreas = signal<boolean>(false);

  // Computed: Check if activity form is collective (to show collectiveFormType)
  isCollectiveForm = computed(() => {
    return this.activityForm() === 'COLLECTIVE' || this.activityForm() === 'COMPANY';
  });

  // Computed: Validation for Section 1
  section1Errors = computed(() => {
    const errors: string[] = [];
    if (!this.activityStartDate()) errors.push('Data Inizio Attività obbligatoria');
    if (!this.professionalOrder()?.trim()) errors.push('Iscrizione Ordine Avvocati obbligatoria');
    if (!this.orderRegistrationNumber()) errors.push('N° Iscrizione obbligatorio');
    return errors;
  });

  // Computed: Validation for Section 2 (sede operativa multi-sede)
  section2Errors = computed(() =>
    validateOperationalSites(this.operatingSameAsLegal(), this.operatingSites()),
  );

  // Computed: Validation for Section 3 (Insurance)
  section3Errors = computed(() => {
    const errors: string[] = [];
    if (!this.rctPolicyNumber()) errors.push('N° Polizza RCT obbligatorio');
    return errors;
  });

  // Computed: Validation for Section 4 (Collaborators)
  section4Errors = computed(() => {
    const errors: string[] = [];
    this.collaborators().forEach((collab, index) => {
      if (!collab.lastName?.trim()) errors.push(`Collaboratore ${index + 1}: Cognome obbligatorio`);
      if (!collab.firstName?.trim()) errors.push(`Collaboratore ${index + 1}: Nome obbligatorio`);
      if (!collab.fiscalCode?.trim()) errors.push(`Collaboratore ${index + 1}: CF obbligatorio`);
      else if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(collab.fiscalCode)) {
        errors.push(`Collaboratore ${index + 1}: CF non valido`);
      }
      if (!collab.professionalOrder?.trim()) {
        errors.push(`Collaboratore ${index + 1}: Ordine Professionale obbligatorio`);
      }
    });
    return errors;
  });

  // Computed: Validation for Section 5 (Competence Areas)
  section5Errors = computed(() => {
    const errors: string[] = [];
    this.competenceAreas().forEach((area, index) => {
      if (!area.region?.trim()) errors.push(`Area ${index + 1}: Regione obbligatoria`);
    });
    return errors;
  });

  // Computed: Overall form validity
  isFormValid = computed(() => {
    return (
      this.section1Errors().length === 0 &&
      this.section2Errors().length === 0 &&
      this.section3Errors().length === 0 &&
      this.section4Errors().length === 0
    );
  });

  ngOnInit(): void {
    this.titleService.setTitle('Portale Fiduciari - Dati Specifici Avvocato');
    this.loadDropdownOptions();
    this.loadExistingData();
  }

  loadDropdownOptions(): void {
    // Load all provinces for operating territories
    this.geographyService
      .getProvinces()
      .pipe(catchError(() => of([])))
      .subscribe((provinces: any) => {
        this.allProvincesOptions.set(
          (provinces || []).map((p: any) => ({ label: p.name, value: p.code }))
        );
      });
  }

  loadExistingData(): void {
    const professionalId = this.professionalIdInput() || this.mockAuthService.getDemoProfessionalId();
    if (!professionalId) return;

    this.isLoadingData.set(true);

    this.professionalsService
      .getLawyerData(professionalId)
      .pipe(
        catchError((error) => {
          console.error('Error loading lawyer data:', error);
          this.isLoadingData.set(false);
          return of(null);
        })
      )
      .subscribe((data) => {
        this.isLoadingData.set(false);
        if (data) {
          this.populateFormData(data.data ?? data);
        } else {
          // No data returned - mark step as NOT completed
          console.warn('⚠️ No lawyer data returned from API');
          this.wizardService.markStepCompleted(1, false);
          this.wizardService.saveState();
        }
      });
  }

  populateFormData(data: any): void {
    console.log('📥 Populating lawyer form with data:', data);

    // Populate Section 1
    if (data.specificData) {
      if (data.specificData.activityStartDate) {
        this.activityStartDate.set(new Date(data.specificData.activityStartDate));
      }
      if (data.specificData.professionalOrder) {
        this.professionalOrder.set(data.specificData.professionalOrder);
      }
      if (data.specificData.orderRegistrationNumber) {
        this.orderRegistrationNumber.set(data.specificData.orderRegistrationNumber);
      }
      if (data.specificData.assignedCompanies) {
        this.assignedCompanies.set(data.specificData.assignedCompanies);
      }
      if (data.specificData.operatingTerritories && Array.isArray(data.specificData.operatingTerritories)) {
        this.operatingTerritories.set(data.specificData.operatingTerritories);
      }

      // Section 2: Activity Form & Headquarters
      if (data.specificData.activityForm) {
        this.activityForm.set(data.specificData.activityForm);
      }
      if (data.specificData.collectiveType) {
        this.collectiveType.set(data.specificData.collectiveType);
      }
      if (data.specificData.firmName) {
        this.firmName.set(data.specificData.firmName);
      }
      // Sede operativa (backend: operationalSites[] multi-sede con flag legalFallback)
      const loadedSites = sitesFromResponse(data.specificData.operationalSites);
      this.operatingSameAsLegal.set(loadedSites.sameAsLegal);
      this.operatingLegalSiteId.set(loadedSites.legalSiteId);
      this.operatingSites.set(loadedSites.sites);

      // Section 3: Insurance
      if (data.specificData.rctPolicyNumber) {
        this.rctPolicyNumber.set(data.specificData.rctPolicyNumber);
      }
      if (data.specificData.insuranceCompany) {
        this.insuranceCompany.set(data.specificData.insuranceCompany);
      }
      if (data.specificData.coverageLimit) {
        this.coverageLimit.set(data.specificData.coverageLimit);
      }
      if (data.specificData.agency) {
        this.agency.set(data.specificData.agency);
      }
    }

    // Section 4: Collaborators
    if (data.collaborators && Array.isArray(data.collaborators) && data.collaborators.length > 0) {
      this.hadPreviousCollaborators.set(true);
      const collabs: CollaboratorUI[] = data.collaborators.map((c: any) => ({
        id: this.collaboratorIdCounter++,
        firstName: c.firstName || '',
        lastName: c.lastName || '',
        fiscalCode: c.fiscalCode || '',
        professionalOrder: c.professionalOrder || '',
        claimsSystemAccess: c.needsClaimsPortalAccess || false,
      }));
      this.collaborators.set(collabs);
    }

    // Section 5: Competence Areas (inside specificData)
    if (data.specificData?.competenceAreas && Array.isArray(data.specificData.competenceAreas) && data.specificData.competenceAreas.length > 0) {
      this.hadPreviousCompetenceAreas.set(true);
      const areas: CompetenceArea[] = data.specificData.competenceAreas.map((a: any) => ({
        id: this.competenceAreaIdCounter++,
        region: a.region || '',
        courtOfAppeal: a.courtOfAppeal || '',
        tribunal: a.tribunal || '',
        justiceOfPeace: a.justiceOfPeace || '',
      }));
      this.competenceAreas.set(areas);
    }

    // Check section status and mark step as completed ONLY if FILLED
    if (data.specificData?.specificDataSectionStatus === 'FILLED') {
      console.log('✅ Specific data section is FILLED - marking step as completed');
      this.wizardService.markStepCompleted(1, true);
    } else {
      console.log('⚠️ Specific data section is NOT filled (status:', data.specificData?.specificDataSectionStatus, ') - marking step as NOT completed');
      this.wizardService.markStepCompleted(1, false);
    }
    
    // Save wizard state to persist the completed status
    this.wizardService.saveState();
  }

  onAssignedCompaniesChange(value: string): void {
    this.assignedCompanies.set(value);
    this.markFieldAsTouched('assignedCompanies');
  }

  markFieldAsTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  // --- CollaboratorUI Management ---
  addCollaborator(): void {
    const newCollaborator: CollaboratorUI = {
      id: this.collaboratorIdCounter++,
      firstName: '',
      lastName: '',
      fiscalCode: '',
      professionalOrder: '',
      civilServant: false,
      publicBody: '',
      communicationOffice: '',
      claimsSystemAccess: false,
    };
    this.collaborators.update((current) => [...current, newCollaborator]);

    this.liveRegionMessage.set('Collaboratore aggiunto');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeCollaborator(collaboratorId: number): void {
    this.collaborators.update((current) => current.filter((c) => c.id !== collaboratorId));

    this.liveRegionMessage.set('Collaboratore rimosso');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  // --- Competence Area Management ---
  addCompetenceArea(): void {
    const newArea: CompetenceArea = {
      id: this.competenceAreaIdCounter++,
      region: '',
      courtOfAppeal: '',
      tribunal: '',
      justiceOfPeace: '',
    };
    this.competenceAreas.update((current) => [...current, newArea]);

    this.liveRegionMessage.set('Area di competenza aggiunta');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  removeCompetenceArea(areaId: number): void {
    this.competenceAreas.update((current) => current.filter((a) => a.id !== areaId));

    this.liveRegionMessage.set('Area di competenza rimossa');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);
  }

  // --- Form Submission ---
  validateAllFields(): boolean {
    this.formSubmitted.set(true);

    // Mark all section 1 fields as touched
    this.touchedFields.add('activityStartDate');
    this.touchedFields.add('professionalOrder');
    this.touchedFields.add('orderRegistrationNumber');
    this.touchedFields.add('assignedCompanies');

    // Mark section 3 fields as touched
    this.touchedFields.add('rctPolicyNumber');

    // Mark CollaboratorUI fields as touched
    this.collaborators().forEach((collab) => {
      this.touchedFields.add(`collab-${collab.id}-lastName`);
      this.touchedFields.add(`collab-${collab.id}-firstName`);
      this.touchedFields.add(`collab-${collab.id}-fiscalCode`);
      this.touchedFields.add(`collab-${collab.id}-professionalOrder`);
    });

    // Mark competence area fields as touched
    this.competenceAreas().forEach((area) => {
      this.touchedFields.add(`area-${area.id}-region`);
    });

    const isValid = this.isFormValid();

    if (!isValid) {
      this.validationFailed.emit(validationSectionsFromMessages([
        { title: 'Informazioni specifiche', errors: this.section1Errors() },
        { title: 'Sede operativa', errors: this.section2Errors() },
        { title: 'Assicurazione', errors: this.section3Errors() },
        { title: 'Aree di competenza', errors: this.section4Errors() },
      ]));
      const allErrors = [
        ...this.section1Errors(),
        ...this.section2Errors(),
        ...this.section3Errors(),
        ...this.section4Errors(),
      ];

      this.liveRegionMessage.set(`Form non valido: ${allErrors.length} errori trovati`);
      setTimeout(() => this.liveRegionMessage.set(''), 3000);
    }

    return isValid;
  }

  buildPayload(): any {
    const payload: any = {
      specificData: {
        activityStartDate: this.activityStartDate()?.toISOString().split('T')[0] || null,
        professionalOrder: this.professionalOrder() || null,
        orderRegistrationNumber: this.orderRegistrationNumber() || null,
        operatingTerritories: this.operatingTerritories(),
        assignedCompanies: this.assignedCompanies() || null,
        rctPolicyNumber: this.rctPolicyNumber() || null,
        insuranceCompany: this.insuranceCompany() || null,
        coverageLimit: this.coverageLimit() || null,
        agency: this.agency() || null,
      },
    };

    // Add activity form and office info if provided
    if (this.activityForm()) {
      payload.specificData.activityForm = this.activityForm();
    }
    if (this.collectiveType()) {
      payload.specificData.collectiveType = this.collectiveType();
    }
    if (this.firmName()) {
      payload.specificData.firmName = this.firmName();
    }

    // Sede operativa (backend: operationalSites[] multi-sede con flag legalFallback)
    payload.specificData.operationalSites = sitesToRequest(
      this.operatingSameAsLegal(),
      this.operatingLegalSiteId(),
      this.operatingSites(),
    );

    // Include competence areas (inside specificData)
    if (this.competenceAreas().length > 0) {
      payload.specificData.competenceAreas = this.competenceAreas().map((area) => ({
        region: area.region || null,
        courtOfAppeal: area.courtOfAppeal || null,
        tribunal: area.tribunal || null,
        justiceOfPeace: area.justiceOfPeace || null,
      }));
    }

    // I collaboratori sono ora gestiti nella sezione dedicata "Impiegati Amministrativi e Collaboratori" del wizard.

    console.log('📤 Lawyer form payload:', payload);

    return payload;
  }

  saveDraft(): void {
    console.log('💾 Saving lawyer draft...');

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();

    // Add section status to specificData
    payload.specificData.specificDataSectionStatus = 'DRAFT';

    this.professionalsService.updateLawyerData(professionalId, payload).subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        console.log('✅ Draft saved successfully:', response);

        this.messageService.add({
          severity: 'success',
          summary: 'Bozza Salvata',
          detail: 'I dati sono stati salvati come bozza',
          life: 5000,
        });

        this.liveRegionMessage.set('Bozza salvata con successo');
        setTimeout(() => this.liveRegionMessage.set(''), 3000);

        this.onSaveDraft.emit(convertEmptyStringsToNull(payload));
      },
      error: (error: any) => {
        this.isSaving.set(false);
        console.error('❌ Error saving draft:', error);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare la bozza',
          life: 5000,
        });
      },
    });
  }

  saveAndContinue(): void {
    console.log('💾 Saving lawyer form and continuing...');

    if (!this.validateAllFields()) {
      return;
    }

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID Fiduciario non trovato',
        life: 5000,
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload();

    // Add section status to specificData
    payload.specificData.specificDataSectionStatus = 'FILLED';

    this.professionalsService.updateLawyerData(professionalId, payload).subscribe({
      next: (response: any) => {
        this.isSaving.set(false);
        console.log('✅ Lawyer data saved successfully:', response);

        this.messageService.add({
          severity: 'success',
          summary: 'Dati Salvati',
          detail: 'I dati specifici sono stati salvati con successo',
          life: 5000,
        });

        this.liveRegionMessage.set('Dati salvati con successo, passaggio allo step successivo');
        setTimeout(() => this.liveRegionMessage.set(''), 3000);

        this.onSave.emit(convertEmptyStringsToNull(payload));
      },
      error: (error: any) => {
        this.isSaving.set(false);
        console.error('❌ Error saving lawyer data:', error);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare i dati',
          life: 5000,
        });
      },
    });
  }
}
